<template>
  <a-modal :title="$t('AccountInfo.BalanceWithdraw.withdrawTitle')"
           :visible="visible"
           :width="500"
           :ok-text="$t('AccountInfo.BalanceWithdraw.withdrawButton')"
           centered
           @ok="handleOk"
           @cancel="handleCancel">
    <a-form ref="form"
            :model="formState"
            :rules="rules">
      <a-form-item :label="$t('AccountInfo.BalanceWithdraw.amountLabel')"
                   name="amount"
                   :colon="false"
                   :rules="[{ required: true, message: $t('AccountInfo.BalanceWithdraw.amountError') }, { validator: validateAmount, trigger: 'blur' }
                  ]">
        <div style="display: flex; gap: 8px;">
          <a-input-number v-model:value="formState.amount"
                          :step="0.1"
                          :min="0.1"
                          :placeholder="$t('AccountInfo.BalanceWithdraw.amountPlaceholder')"
                          style="flex: 1;" />
          <a-button type="link"
                    class="defaultText"
                    @click="withdrawAll">{{ $t('AccountInfo.BalanceWithdraw.withdrawAll') }}</a-button>
        </div>
      </a-form-item>
      <a-form-item :label="$t('AccountInfo.BalanceWithdraw.paymentMethodLabel')"
                   name="method"
                   :colon="false"
                   :rules="[{ required: true, message: $t('AccountInfo.BalanceWithdraw.paymentMethodError') }]">
        <a-radio-group v-model:value="formState.method">
          <a-radio class="defaultText"
                   :value=1>{{ $t('AccountInfo.BalanceWithdraw.alipay') }}</a-radio>
        </a-radio-group>
      </a-form-item>
      <a-form-item v-if="Number(formState.method) === 1"
                   label=""
                   name="userAccount"
                   :colon="false"
                   :rules="[{ required: true, message: $t('AccountInfo.BalanceWithdraw.userAccountError') }]">
        <a-input v-model:value="formState.userAccount"
                 :placeholder="$t('AccountInfo.BalanceWithdraw.userAccountPlaceholder')"
                 style="width: 100%;" />
      </a-form-item>
      <a-form-item v-if="Number(formState.method) === 1"
                   label=""
                   name="userAccountName"
                   :colon="false"
                   :rules="[{ required: true, message: $t('AccountInfo.BalanceWithdraw.userAccountNameError') }]">
        <a-input v-model:value="formState.userAccountName"
                 :placeholder="$t('AccountInfo.BalanceWithdraw.userAccountNamePlaceholder')"
                 style="width: 100%;" />
      </a-form-item>
    </a-form>
  </a-modal>

  <a-modal :title="$t('AccountInfo.BalanceWithdraw.alertTitle')"
           :visible="alertVisible"
           :width="350"
           :ok-text="$t('AccountInfo.BalanceWithdraw.alertOk')"
           centered
           @ok="handleAlertOk"
           @cancel="handleAlertOk">
    <p>
      <span class="icon-placeholder"></span>{{ $t('AccountInfo.BalanceWithdraw.alertMessage') }}
    </p>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { WithdrawRequestParams } from '../api/interface/balanceInterface'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  account: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'submit'])
const form = ref<any>(null)
const { t } = useI18n()
const formState = ref<WithdrawRequestParams>({
  amount: 1,
  method: 1,
  userAccount: '',
  userAccountName: '',
})

const alertVisible = ref(false)

// 定义验证规则
const rules = computed(() => ({
  amount: [{ required: true, message: t('AccountInfo.BalanceWithdraw.amountError'), trigger: 'blur' }],
  method: [{ required: true, message: t('AccountInfo.BalanceWithdraw.paymentMethodError'), trigger: 'change' }],
  userAccount: [{ required: true, message: t('AccountInfo.BalanceWithdraw.userAccountError'), trigger: 'blur' }],
  userAccountName: [{ required: true, message: t('AccountInfo.BalanceWithdraw.userAccountNameError'), trigger: 'blur' }],
}))

const handleSubmit = () => {
  formState.value.amount = Number(formState.value.amount)

  form.value?.validate().then(() => {
    if (formState.value.amount <= 100) {
      handleCancel()
      alertVisible.value = true
      throw new Error("Amount less than 100")
    }
    emit('submit', { amount: formState.value.amount, method: Number(formState.value.method), userAccount: formState.value.userAccount, userAccountName: formState.value.userAccountName })
  }).catch((errorInfo: string) => {
    console.error('Validate Failed:', errorInfo)
  })
}

const handleOk = () => {
  handleSubmit()
}

const handleAlertOk = () => {
  alertVisible.value = false
}

const handleCancel = () => {
  emit('close')
}

const withdrawAll = () => {
  formState.value.amount = Number(props.account.balance)
}

const validateAmount = (rule: any, value: number, callback: any) => {
  if (value <= 0) {
    callback(new Error(t('AccountInfo.BalanceWithdraw.amountLessThan100Error')))
  } else {
    callback()
  }
}

watch(() => formState.value.method, (newMethod) => {
  if (Number(newMethod) === 1) {
    rules.value.userAccount[0].required = true
    rules.value.userAccountName[0].required = true
  } else {
    rules.value.userAccount[0].required = false
    rules.value.userAccountName[0].required = false
    formState.value.userAccount = ''
    formState.value.userAccountName = ''
  }
})
</script>

<style scoped lang="less">
.defaultText {
  font-family: Microsoft YaHei;
  font-weight: 400;
  font-size: 14px;
  color: #999999;
}

:deep(.ant-form-item-label) {
  font-family: Microsoft YaHei;
  font-weight: 400;
  font-size: 14px;
  color: #111111;
}

:deep(.ant-radio-wrapper-checked) {
  color: #111111;
}

.icon-placeholder {
  display: inline-block;
  background: url("../view/assets/imgs/balanceWithdrawWarning.png") no-repeat;
  background-size: 100% 100%;
  width: 20px;
  height: 20px;
  margin-right: 8px;

  vertical-align: middle;
}
</style>
